import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  closeImageFrame,
  loadFrameFromImage,
  type ImageFrame,
} from "../../utils/loadFrameFromImage";

interface TruckImageSequenceProps {
  images: string[];
  targetFrame: number;
  duration?: number;
  className?: string;
  alt?: string;
}

type CachedFrame = ImageFrame;
type CacheEntry = {
  frames: (CachedFrame | undefined)[];
  status: "loading" | "loaded";
  fullPromise: Promise<void>;
  isPartial?: boolean;
};

const imageCache = new Map<string, CacheEntry>();

const runWithConcurrency = async <T,>(
  tasks: Array<() => Promise<T>>,
  limit: number
): Promise<T[]> => {
  if (!tasks.length) return [];

  const results: T[] = new Array(tasks.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < tasks.length) {
      const current = nextIndex++;
      results[current] = await tasks[current]();
    }
  };

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () => worker());
  await Promise.all(workers);
  return results;
};

const closeCacheEntry = (cached: CacheEntry) => {
  if (!cached?.frames) return;
  for (let i = 0; i < cached.frames.length; i++) {
    closeImageFrame(cached.frames[i]);
    cached.frames[i] = undefined;
  }
};

export const getImageSetKey = (images: string[]) => {
  if (!images.length) return "";
  return `${images[0]}_${images[images.length - 1]}_${images.length}`;
};

export const preloadImageSet = (images: string[]): void => {
  preloadImageSetAsync(images);
};

export const preloadImageSetAsync = (images: string[], priorityFrames: number[] = [], loadOnlyPriority = false): Promise<void> => {
  if (!images.length) return Promise.resolve();
  const key = getImageSetKey(images);
  const cached = imageCache.get(key);

  if (cached?.status === "loaded") return Promise.resolve();
  
  if (cached?.status === "loading") {
    // If we have a partial load and now we want a full load, we need to continue
    if (cached.isPartial && !loadOnlyPriority) {
      // Fall through to start a full load
    } else {
      if (priorityFrames.length > 0) {
        return new Promise<void>((resolve) => {
          const check = () => {
            const allReady = priorityFrames.every(i => cached.frames[i] !== undefined);
            if (allReady || cached.status === "loaded") resolve();
            else setTimeout(check, 50);
          };
          check();
        });
      }
      return cached.fullPromise;
    }
  }

  const frames: (CachedFrame | undefined)[] = cached?.frames ?? new Array(images.length);
  const fullPromise = new Promise<void>((resolveFull) => {
    // If we're upgrading from partial, we need to know how many are already loaded
    // or just count correctly. 
    // Actually, count should be based on what we are CURRENTLY loading.
    
    let count = 0;
    const targetCount = loadOnlyPriority ? priorityFrames.length : images.length;

    const checkAndResolveWhenAllLoaded = () => {
      if (++count === targetCount) {
        if (!loadOnlyPriority) {
          const isMobile = typeof window !== "undefined" && window.innerWidth < 840;
          if (!isMobile) {
            // Warm up: draw each frame once to a tiny canvas so that GPU textures
            // (or CPU decode caches) are primed before the first animation runs.
            const warmup = document.createElement("canvas");
            warmup.width = 1;
            warmup.height = 1;
            const ctx = warmup.getContext("2d");
            if (ctx) {
              ctx.globalCompositeOperation = "copy";
              for (const frame of frames) {
                if (frame) ctx.drawImage(frame as CanvasImageSource, 0, 0, 1, 1);
              }
            }
          }

          const currentEntry = imageCache.get(key);
          if (currentEntry) {
            currentEntry.status = "loaded";
            currentEntry.isPartial = false;
          }
        }
        resolveFull();
      }
    };

    const loadIndex = (i: number): Promise<void> =>
      new Promise((resolve) => {
        if (frames[i] !== undefined) {
          checkAndResolveWhenAllLoaded();
          resolve();
          return;
        }

        const src = images[i];
        const img = new Image();
        let finalized = false;

        const finalize = (frame: CachedFrame | undefined) => {
          if (finalized) return;
          finalized = true;
          if (!imageCache.has(key)) {
            closeImageFrame(frame);
            resolve();
            return;
          }
          frames[i] = frame;
          checkAndResolveWhenAllLoaded();
          resolve();
        };

        img.onload = () => {
          loadFrameFromImage(img)
            .then(finalize)
            .catch(() => finalize(undefined));
        };
        img.onerror = () => finalize(undefined);
        img.src = src;
      });

    const queueLoads = async (indices: number[]) => {
      const uniqueIndices = Array.from(new Set(indices.filter(i => i >= 0 && i < images.length)));
      if (!uniqueIndices.length) {
        if (loadOnlyPriority) resolveFull();
        return;
      }

      const concurrency = typeof window !== "undefined" && window.innerWidth < 840 ? 2 : 6;
      await runWithConcurrency(
        uniqueIndices.map((i) => () => loadIndex(i)),
        concurrency
      );
    };

    const validPriorityFrames = Array.from(new Set(priorityFrames.filter(i => i >= 0 && i < images.length)));

    if (validPriorityFrames.length === 0) {
      if (loadOnlyPriority) {
        resolveFull();
      } else {
        void queueLoads(Array.from({ length: images.length }, (_, i) => i));
      }
    } else {
      void (async () => {
        await queueLoads(validPriorityFrames);
        if (!loadOnlyPriority) {
          const remaining = Array.from({ length: images.length }, (_, i) => i)
            .filter(i => !validPriorityFrames.includes(i));
          await queueLoads(remaining);
        }
      })();
    }
  });

  imageCache.set(key, { frames, status: "loading", fullPromise, isPartial: loadOnlyPriority });

  if (priorityFrames.length > 0) {
    return new Promise<void>((resolve) => {
      const check = () => {
        const allReady = priorityFrames.every((i) => frames[i] !== undefined);
        const entry = imageCache.get(key);
        if (allReady || entry?.status === "loaded") resolve();
        else setTimeout(check, 50);
      };
      check();
    });
  }

  return fullPromise;
};

// Frees all frames for an image set and removes it from the cache.
// Must be called when a truck's images are no longer needed (e.g. power-type
// switch) to prevent unbounded memory growth.
export const evictImageSet = (images: string[]): void => {
  if (!images.length) return;
  const key = getImageSetKey(images);
  const cached = imageCache.get(key);
  if (cached) closeCacheEntry(cached);
  imageCache.delete(key);
};

export const evictAllImageSets = (): void => {
  imageCache.forEach((cached) => closeCacheEntry(cached));
  imageCache.clear();
};

// Paints a frame to the canvas at display resolution (not source resolution).
// The canvas backing-store is kept in sync with CSS clientWidth/clientHeight so
// we never allocate or process more pixels than are actually visible on screen.
const paintFrame = (canvas: HTMLCanvasElement, frame: CachedFrame) => {
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (w === 0 || h === 0) return;

  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const srcW = frame instanceof ImageBitmap ? frame.width : frame.naturalWidth;
  const srcH = frame instanceof ImageBitmap ? frame.height : frame.naturalHeight;
  if (srcW === 0 || srcH === 0) return;

  // object-fit: contain
  const scale = Math.min(w / srcW, h / srcH);
  const dw = srcW * scale;
  const dh = srcH * scale;
  const dx = (w - dw) / 2;
  const dy = (h - dh) / 2;

  // "copy" replaces every pixel in one atomic draw — no clearRect flash,
  // and PNG transparent areas become genuinely transparent.
  ctx.globalCompositeOperation = "copy";
  ctx.drawImage(frame as CanvasImageSource, dx, dy, dw, dh);
};

export const TruckImageSequence = ({
  images,
  targetFrame,
  duration = 600,
  className = "",
  alt = "Truck model"
}: TruckImageSequenceProps) => {
  const imageSetKey = getImageSetKey(images);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  const currentFrameRef = useRef(Math.max(0, Math.min(targetFrame, images.length - 1)));
  const animationRef = useRef<number | null>(null);

  const repaint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cached = imageCache.get(imageSetKey);
    if (cached && cached.frames && cached.frames[currentFrameRef.current]) {
      paintFrame(canvas, cached.frames[currentFrameRef.current] as CachedFrame);
    }
  }, [imageSetKey]);

  // Preload images for this sequence. Re-runs only when imageSetKey changes.
  useEffect(() => {
    if (!images.length) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    let cancelled = false;
    const initialFrame = Math.max(0, Math.min(targetFrame, images.length - 1));
    preloadImageSetAsync(images, [initialFrame]).then(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [imageSetKey]);

  // Draw initial frame before first paint to avoid a blank-canvas flash.
  useLayoutEffect(() => {
    if (!isLoading) repaint();
  }, [isLoading, repaint]);

  // When the image set changes, cancel any ongoing animation and snap to the initial frame.
  useEffect(() => {
    if (!images.length) return;
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    const initialFrame = Math.max(0, Math.min(targetFrame, images.length - 1));
    currentFrameRef.current = initialFrame;
    repaint();
  }, [imageSetKey]); // intentional: snap only on set change, not on every targetFrame update

  // Animate from current position to targetFrame.
  useEffect(() => {
    if (!images.length) return;

    const safeTarget = Math.max(0, Math.min(targetFrame, images.length - 1));

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (duration <= 0 || safeTarget === currentFrameRef.current) {
      currentFrameRef.current = safeTarget;
      repaint();
      return;
    }

    const start = performance.now();
    const initial = currentFrameRef.current;
    const delta = safeTarget - initial;

    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const rawFrame = progress >= 1 ? safeTarget : initial + delta * progress;
      const frame = Math.max(0, Math.min(images.length - 1, Math.round(rawFrame)));

      if (frame !== currentFrameRef.current) {
        currentFrameRef.current = frame;
        repaint();
      }

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(step);
      } else {
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(step);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [targetFrame, duration, imageSetKey, repaint]);

  return (
    <div
      className={`relative w-full h-full flex items-center justify-center ${className}`}
    >
      <canvas
        ref={canvasRef}
        aria-label={alt}
        role="img"
        style={{ width: "100%", height: "100%", visibility: isLoading ? "hidden" : "visible" }}
      />
    </div>
  );
};
